import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import {
  AgentAuditOutcome,
  ContractStateDto,
  type EventListItemDto,
  useRecordAgentAuditEntryMutation,
} from "../../autogen/bff-api";
import { ddRum } from "../../datadog";
import { useEventNavigation } from "../../pages/sourcing-events/useEventNavigation";
import { urls } from "../../urls";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useFency } from "../fency/useFency";
import { useAppDispatch } from "../redux/hooks";
import type { AgentAction } from "./AgentActionSchema";
import type { AgentToolDescriptor, RiskTier } from "./agentTypes";
import type { PendingConfirmation } from "./ConfirmationCard";
import { buildAnswerSystemPrompt, buildDraftSystemPrompt, buildSystemPrompt } from "./systemPrompt";
import { boundForPlanner, DRAFT_TOOL_NAME, runTool, SEARCH_TOOL_NAME } from "./tools";
import type { ToolContext, ToolResult } from "./tools";
import { useAgentSession } from "./useAgentSession";
import { useDocumentSearch } from "./useDocumentSearch";
import { usePlannerCompletion } from "./usePlannerCompletion";

const AGENT_MODEL: AgentTaskModel = "anthropic/claude-sonnet-4.6";
const MAX_STEPS = 6;
const ANSWER_TIMEOUT_MS = 90_000;
const MEMORY_MAX_CHARS = 12000;

// Small explicit adapter (like ARG_ADAPTERS): a few mutations should land the user on the entity
// they just created — an app route the OpenAPI spec can't describe. Maps operationId → the route
// for the result's id.
const RESULT_NAV: Record<string, ((id: string) => string) | undefined> = {
  createSourcingEvent: (id) => {
    const { pathname, search } = urls.publicEvents.edit.go(id).info;
    return `${pathname}?${search}`;
  },
};

// Client-side tools (no BFF endpoint), described like any catalog tool for the prompt.
const SEARCH_TOOL: AgentToolDescriptor = {
  toolName: SEARCH_TOOL_NAME,
  method: "POST",
  riskTier: "none",
  description: "Søker i organisasjonens konkurransedokumenter etter relevante avsnitt.",
  params: [{ name: "query", in: "query", type: "string", required: true }],
};

// Generates a ready-to-use text draft (no mutation) the user can take and use, or later ask
// the agent to apply via a mutation tool.
const DRAFT_TOOL: AgentToolDescriptor = {
  toolName: DRAFT_TOOL_NAME,
  method: "POST",
  riskTier: "none",
  description: "Lager et tekstutkast (svar til leverandør, tildelings-/avslagsbrev, beskrivelse) som brukeren kan bruke.",
  params: [
    {
      name: "kind",
      in: "query",
      type: "qa_answer | award_letter | rejection_letter | event_description | general",
      required: true,
    },
    { name: "instruction", in: "query", type: "string", required: true },
    { name: "context", in: "query", type: "string", required: false },
  ],
};

type TurnMessage = { role: "SYSTEM" | "USER" | "ASSISTANT"; content: string };

type ConfirmDecision =
  | { kind: "confirm"; finalArgs: Record<string, unknown> }
  | { kind: "cancel" }
  | { kind: "supersede" };

export type ToolStep = { label: string; ok: boolean };
export type AgentTurn = {
  user: string;
  steps: ToolStep[];
  answerIndex: number | null;
  note: string | null;
};

// The planner replies as plain text; pull the action JSON object out of it (tolerating code
// fences or stray prose).
const extractJson = (text: string): string => {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  const source = fenced ? fenced[1] : text;
  const start = source.indexOf("{");
  if (start === -1) {
    return source.trim();
  }
  let depth = 0;
  for (let i = start; i < source.length; i += 1) {
    if (source[i] === "{") {
      depth += 1;
    } else if (source[i] === "}") {
      depth -= 1;
      if (depth === 0) {
        return source.slice(start, i + 1);
      }
    }
  }
  return source.slice(start);
};

const parseAgentAction = (json: string): AgentAction | null => {
  try {
    return JSON.parse(json) as AgentAction;
  } catch {
    return null;
  }
};

// Index entities returned by read tools by their OWN primary id, recursing into nested lists, so
// the agent can navigate to / confirm an action on a specific one by id, and the card can resolve
// an id to a human label. The own id is inferred from the response shape — a `kind` field marks a
// sourcing event, a contract carries a contract `state` — rather than any property merely named
// id/contractId/bidId, so a foreign-key field (e.g. a bid's contractId) can't shadow the real
// contract with that id.
const indexEntity = (obj: Record<string, unknown>, into: Map<string, Record<string, unknown>>): void => {
  if (typeof obj.kind === "string") {
    if (typeof obj.id === "string") {
      into.set(obj.id, obj);
    }
    return;
  }
  if (typeof obj.id === "string") {
    into.set(obj.id, obj);
  }
  if (typeof obj.bidId === "string") {
    into.set(obj.bidId, obj);
  }
  // `contractId` is the entity's own id only on a contract (which carries a contract `state`); on a
  // bid or event it is a foreign key, so it must not shadow the real contract.
  if (typeof obj.contractId === "string" && "state" in obj) {
    into.set(obj.contractId, obj);
  }
};

const indexEntities = (data: unknown, into: Map<string, Record<string, unknown>>): void => {
  if (Array.isArray(data)) {
    data.forEach((item) => indexEntities(item, into));
    return;
  }
  if (!data || typeof data !== "object") {
    return;
  }
  const obj = data as Record<string, unknown>;
  indexEntity(obj, into);
  Object.values(obj).forEach((value) => {
    if (Array.isArray(value)) {
      indexEntities(value, into);
    }
  });
};

const parseArgs = (argsJson: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(argsJson || "{}");
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export const useAssistant = () => {
  const authState = useLoggedInWithOrgContextState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const navigateToEvent = useEventNavigation();
  const entityIndex = useRef(new Map<string, Record<string, unknown>>());

  const organizationId = authState.selectedOrg.id;
  const personId = authState.me.id;

  const tools = useMemo(() => [...agentToolCatalog, SEARCH_TOOL, DRAFT_TOOL], []);
  const lookupEntity = useCallback((id: string) => entityIndex.current.get(id), []);

  // Fency is stateless, so we carry a bounded record of what we've already fetched across turns
  // and feed it back to the planner — so a follow-up reuses earlier data instead of re-listing.
  const conversationMemory = useRef<string[]>([]);
  const rememberResult = useCallback((toolName: string, result: ToolResult) => {
    conversationMemory.current.push(`${toolName}: ${boundForPlanner(result)}`);
    let total = conversationMemory.current.reduce((sum, entry) => sum + entry.length, 0);
    while (total > MEMORY_MAX_CHARS && conversationMemory.current.length > 1) {
      total -= conversationMemory.current.shift()?.length ?? 0;
    }
  }, []);

  const { fetchDocSearchToken } = useAgentSession();
  const { fetchCreateStreamingChatCompletionClientToken } = useFency();
  const runPlanner = usePlannerCompletion({
    fetchClientToken: fetchCreateStreamingChatCompletionClientToken,
    timeoutMs: 60_000,
  });
  const searchDocuments = useDocumentSearch({ fetchClientToken: fetchDocSearchToken });

  // Drafting reuses the streaming-completion path with a domain-specific system prompt; it
  // returns text, never touches an endpoint, and changes nothing.
  const generateDraft = useCallback(
    async (args: Record<string, unknown>): Promise<ToolResult> => {
      const kind = String(args.kind ?? "general");
      const instruction = String(args.instruction ?? "");
      const context = String(args.context ?? "");
      if (!instruction.trim()) {
        return { ok: false, deviationCode: "MISSING_INSTRUCTION", message: "Mangler instruksjon for utkastet." };
      }
      const messages: TurnMessage[] = [
        { role: "SYSTEM", content: buildDraftSystemPrompt(kind) },
        { role: "USER", content: context.trim() ? `${instruction}\n\nKontekst:\n${context}` : instruction },
      ];
      try {
        const draft = await runPlanner(messages, AGENT_MODEL);
        return { ok: true, data: { draft } };
      } catch {
        return { ok: false, deviationCode: "DRAFT_FAILED", message: "Klarte ikke å lage utkast." };
      }
    },
    [runPlanner],
  );

  // Server-side audit of every proposed agent action and its outcome (best-effort: a failed audit
  // write must never break the turn).
  const [recordAgentAuditEntry] = useRecordAgentAuditEntryMutation();
  const conversationId = useRef(crypto.randomUUID());
  const recordAudit = useCallback(
    (
      outcome: AgentAuditOutcome,
      p: {
        toolName: string;
        riskTier: RiskTier;
        args: Record<string, unknown>;
        turnId: number;
        userInstruction: string;
        deviationCode?: string;
      },
    ) => {
      void recordAgentAuditEntry({
        agentAuditEntryDto: {
          conversationId: conversationId.current,
          turnId: String(p.turnId),
          eventId: typeof p.args.eventId === "string" ? p.args.eventId : null,
          toolName: p.toolName,
          riskTier: p.riskTier,
          outcome,
          proposedArgs: JSON.stringify(p.args),
          deviationCode: p.deviationCode ?? null,
          userInstruction: p.userInstruction,
        },
      })
        .unwrap()
        .catch((error) =>
          ddRum.addError(error, { source: "agent-audit", toolName: p.toolName, outcome, riskTier: p.riskTier }),
        );
    },
    [recordAgentAuditEntry],
  );

  const answerWaiters = useRef<Array<() => void>>([]);
  const resolveAnswerWaiters = useCallback(() => {
    answerWaiters.current.forEach((resolve) => resolve());
    answerWaiters.current = [];
  }, []);

  const fetchStreamingToken = useCallback(
    () => fetchCreateStreamingChatCompletionClientToken(),
    [fetchCreateStreamingChatCompletionClientToken],
  );

  const { createAgentTask, agentTasks } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchStreamingToken,
    onAgentTaskProgressItemUpdated: ({ data }) => {
      if (data.taskType === "StreamingChatCompletion" && (data.eventType === "Completed" || data.eventType === "Error")) {
        resolveAnswerWaiters();
      }
    },
    onStreamError: () => resolveAnswerWaiters(),
  });

  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const streamIndexRef = useRef(0);

  // A pending mutation pauses the loop: the human confirms (optionally editing the args) or cancels
  // at the confirmation card; confirm()/cancel() resolve the promise the loop is awaiting.
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  const confirmResolver = useRef<((decision: ConfirmDecision) => void) | null>(null);

  const waitForConfirmation = useCallback((pending: PendingConfirmation): Promise<ConfirmDecision> => {
    setPendingConfirmation(pending);
    setActiveStep(null);
    return new Promise<ConfirmDecision>((resolve) => {
      confirmResolver.current = resolve;
    });
  }, []);

  const settle = useCallback((decision: ConfirmDecision) => {
    setPendingConfirmation(null);
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    resolve?.(decision);
  }, []);

  const confirm = useCallback(
    (finalArgs: Record<string, unknown>) => settle({ kind: "confirm", finalArgs }),
    [settle],
  );

  const cancel = useCallback(() => settle({ kind: "cancel" }), [settle]);

  // The user kept chatting instead of confirming: abandon the proposal and queue the new message
  // as a fresh turn, so the agent re-proposes with the added context.
  const refine = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !confirmResolver.current) {
        return;
      }
      setQueuedMessage(trimmed);
      settle({ kind: "supersede" });
    },
    [settle],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isRunning) {
        return;
      }

      const turnIndex = turns.length;
      const updateTurn = (patch: (turn: AgentTurn) => AgentTurn) =>
        setTurns((prev) => prev.map((turn, i) => (i === turnIndex ? patch(turn) : turn)));

      setTurns((prev) => [...prev, { user: trimmed, steps: [], answerIndex: null, note: null }]);
      setIsRunning(true);
      setActiveStep(null);

      const history: TurnMessage[] = turns.flatMap((turn) =>
        turn.note
          ? [
              { role: "USER" as const, content: turn.user },
              { role: "ASSISTANT" as const, content: turn.note },
            ]
          : [{ role: "USER" as const, content: turn.user }],
      );
      const priorMemory = conversationMemory.current.join("\n");
      const memory: TurnMessage[] = priorMemory
        ? [
            {
              role: "USER",
              content:
                "Kontekst fra tidligere i samtalen (data du allerede har hentet — ikke hent på nytt hvis dette holder for å svare):\n" +
                priorMemory,
            },
          ]
        : [];
      let convo: TurnMessage[] = [
        { role: "SYSTEM", content: buildSystemPrompt(tools, { organizationId, personId }) },
        ...history,
        ...memory,
        { role: "USER", content: trimmed },
      ];
      const ctx: ToolContext = { dispatch, auth: { organizationId, personId } };
      const toolContext: string[] = [];
      const seenCalls = new Set<string>();

      try {
        let outcome: "stream" | "navigate" | "aborted" = "stream";

        for (let step = 0; step < MAX_STEPS; step++) {
          setActiveStep("Tenker");
          const planText = await runPlanner(convo, AGENT_MODEL);
          const json = extractJson(planText);
          const action = parseAgentAction(json);

          if (!action || action.kind === "message") {
            outcome = "stream";
            break;
          }
          if (action.kind === "navigate") {
            const targetId = String((parseArgs(action.argsJson).id as string | undefined) ?? "");
            const entity = targetId ? entityIndex.current.get(targetId) : undefined;
            let navigated = true;
            if (targetId && entity && typeof entity.kind === "string") {
              navigateToEvent(entity as unknown as EventListItemDto);
            } else if (targetId) {
              const contractId = typeof entity?.contractId === "string" ? entity.contractId : targetId;
              navigate(
                entity?.state === ContractStateDto.Draft
                  ? urls.contracts.edit.go(contractId).details
                  : urls.contracts.viewSingle.go(contractId),
              );
            } else if (action.route) {
              navigate(action.route);
            } else {
              navigated = false;
            }
            updateTurn((turn) => ({ ...turn, note: navigated ? "Åpner siden." : "Fant ikke siden å åpne." }));
            outcome = "navigate";
            break;
          }

          const descriptor = tools.find((tool) => tool.toolName === action.toolName);
          const label = descriptor?.description ?? action.toolName;
          const args = parseArgs(action.argsJson);
          const riskTier = descriptor?.riskTier ?? "none";

          if (descriptor && riskTier !== "none") {
            recordAudit(AgentAuditOutcome.Proposed, { toolName: action.toolName, riskTier, args, turnId: turnIndex, userInstruction: trimmed });
            const decision = await waitForConfirmation({ label, riskTier, descriptor, args });
            if (decision.kind === "supersede") {
              recordAudit(AgentAuditOutcome.Rejected, { toolName: action.toolName, riskTier, args, turnId: turnIndex, userInstruction: trimmed });
              updateTurn((turn) => ({ ...turn, note: "Justerer forslaget …" }));
              outcome = "aborted";
              break;
            }
            if (decision.kind === "cancel") {
              recordAudit(AgentAuditOutcome.Rejected, { toolName: action.toolName, riskTier, args, turnId: turnIndex, userInstruction: trimmed });
              updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label: `${label} – avbrutt`, ok: false }] }));
              toolContext.push(`${action.toolName}: Brukeren avbrøt handlingen. Ingen endring ble utført.`);
              outcome = "stream";
              break;
            }

            recordAudit(AgentAuditOutcome.Confirmed, {
              toolName: action.toolName,
              riskTier,
              args: decision.finalArgs,
              turnId: turnIndex,
              userInstruction: trimmed,
            });
            setActiveStep(label);
            const result = await runTool(action.toolName, decision.finalArgs, ctx, searchDocuments, generateDraft);
            if (result.ok) {
              indexEntities(result.data, entityIndex.current);
            }
            updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label, ok: result.ok }] }));
            recordAudit(result.ok ? AgentAuditOutcome.Executed : AgentAuditOutcome.Failed, {
              toolName: action.toolName,
              riskTier,
              args: decision.finalArgs,
              turnId: turnIndex,
              userInstruction: trimmed,
              deviationCode: result.ok ? undefined : result.deviationCode,
            });
            toolContext.push(`${action.toolName}: ${JSON.stringify(result)}`);
            const navTo = RESULT_NAV[action.toolName];
            const created = navTo && result.ok ? (result.data as { id?: unknown }) : undefined;
            if (navTo && typeof created?.id === "string") {
              navigate(navTo(created.id));
              updateTurn((turn) => ({ ...turn, note: "Opprettet — åpner den." }));
              outcome = "navigate";
              break;
            }
            outcome = "stream";
            break;
          }

          // The planner sometimes repeats an identical call instead of converging; stop the loop
          // and answer with what we already have rather than spinning to MAX_STEPS.
          const callKey = `${action.toolName}:${action.argsJson}`;
          if (seenCalls.has(callKey)) {
            outcome = "stream";
            break;
          }
          seenCalls.add(callKey);

          setActiveStep(label);
          const result = await runTool(action.toolName, args, ctx, searchDocuments, generateDraft);
          if (result.ok) {
            indexEntities(result.data, entityIndex.current);
          }
          updateTurn((turn) => ({ ...turn, steps: [...turn.steps, { label, ok: result.ok }] }));
          toolContext.push(`${action.toolName}: ${JSON.stringify(result)}`);
          rememberResult(action.toolName, result);
          convo = [
            ...convo,
            { role: "ASSISTANT", content: json },
            { role: "USER", content: `TOOL_RESULT(${action.toolName}): ${boundForPlanner(result)}` },
          ];
        }

        if (outcome === "stream") {
          setActiveStep("Skriver svar");
          const answerIndex = streamIndexRef.current;
          streamIndexRef.current += 1;
          updateTurn((turn) => ({ ...turn, answerIndex }));

          const answerMessages: TurnMessage[] = [
            { role: "SYSTEM", content: buildAnswerSystemPrompt() },
            {
              role: "USER",
              content: `Spørsmål: ${trimmed}\n\nData hentet fra systemet:\n${
                [
                  priorMemory ? `Tidligere i samtalen:\n${priorMemory}` : "",
                  toolContext.length ? `Hentet nå:\n${toolContext.join("\n\n")}` : "",
                ]
                  .filter(Boolean)
                  .join("\n\n") || "(ingen verktøy ble brukt)"
              }`,
            },
          ];

          const answerDone = new Promise<void>((resolve) => answerWaiters.current.push(resolve));
          const answerTask = createAgentTask({
            type: "StreamingChatCompletion",
            messages: answerMessages,
            model: AGENT_MODEL,
          });
          await Promise.race([
            answerTask.then(
              () => undefined,
              () => undefined,
            ),
            answerDone,
            new Promise<void>((resolve) => setTimeout(resolve, ANSWER_TIMEOUT_MS)),
          ]);
        }
      } finally {
        setActiveStep(null);
        setIsRunning(false);
      }
    },
    [
      isRunning,
      turns,
      tools,
      recordAudit,
      waitForConfirmation,
      dispatch,
      navigate,
      navigateToEvent,
      organizationId,
      personId,
      runPlanner,
      searchDocuments,
      generateDraft,
      rememberResult,
      createAgentTask,
    ],
  );

  // When a turn finishes, flush a message the user typed while a confirmation card was up — it
  // supersedes the proposal and starts a fresh turn that re-proposes with the new info.
  useEffect(() => {
    if (!isRunning && queuedMessage !== null) {
      const next = queuedMessage;
      setQueuedMessage(null);
      void send(next);
    }
  }, [isRunning, queuedMessage, send]);

  return {
    turns,
    streamingTasks: agentTasks,
    isRunning,
    activeStep,
    pendingConfirmation,
    confirm,
    cancel,
    refine,
    lookupEntity,
    send,
  };
};
